import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, CheckCircle, Code2, DollarSign, Users, Rocket } from 'lucide-react';

const HowItWorks = () => {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen">
      <div className="circuit-bg" />
      <div className="circuit-overlay" />
      
      <div className="content-wrapper">
        {/* Navigation */}
        <nav className="border-b border-green-500/20 backdrop-blur-md" data-testid="how-navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-green-400 hover:text-green-300"
                data-testid="back-button"
              >
                <ArrowLeft className="mr-2 w-5 h-5" />
                Back to Home
              </Button>
            </div>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <h1 className="text-5xl md:text-6xl font-bold text-center mb-8 glow-text" data-testid="page-title">
            HOW IT WORKS
          </h1>
          
          <p className="text-xl text-green-300 text-center mb-16 leading-relaxed" data-testid="page-subtitle">
            The Millionaire Machine is a replicable, ethical wealth-generation system. 
            Here's exactly how it works:
          </p>

          {/* The Concept */}
          <section className="mb-16" data-testid="concept-section">
            <h2 className="text-3xl font-bold mb-6 text-green-400 glow-text">THE CONCEPT</h2>
            <Card className="card-tech p-8">
              <p className="text-green-300 text-lg leading-relaxed mb-4">
                The Millionaire Machine operates on a simple but powerful principle: 
                <span className="text-green-400 font-bold"> information has value</span>.
              </p>
              <p className="text-green-300 text-lg leading-relaxed mb-4">
                For just $1, users gain access to:
              </p>
              <ul className="space-y-3 ml-6">
                {[
                  'Complete open-source code',
                  'Step-by-step setup instructions',
                  'PayPal integration playbook',
                  'Encryption & credential security guide',
                  'Marketing and distribution strategies',
                  'Legal and ethical guidelines'
                ].map((item, idx) => (
                  <li key={item} className="flex items-start text-green-300 text-lg" data-testid={`concept-item-${idx}`}>
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </Card>
          </section>

          {/* The Steps */}
          <section className="mb-16" data-testid="steps-section">
            <h2 className="text-3xl font-bold mb-8 text-green-400 glow-text">THE 6-STEP PROCESS</h2>
            
            <div className="space-y-6">
              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="step-1">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-green-500">1</span>
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-2xl font-bold mb-3 text-green-400">Pay $1 & Get Access</h3>
                    <p className="text-green-300 text-lg leading-relaxed">
                      You pay $1 via PayPal and instantly receive access to the complete Millionaire Machine 
                      repository, documentation, and all setup files.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="step-2">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-green-500">2</span>
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-2xl font-bold mb-3 text-green-400">Clone the Repository</h3>
                    <p className="text-green-300 text-lg leading-relaxed mb-4">
                      Fork or clone the GitHub repository to your local machine or hosting platform.
                    </p>
                    <div className="code-block">
                      <code>git clone https://github.com/yourusername/millionaire-machine.git</code>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="step-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-green-500">3</span>
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-2xl font-bold mb-3 text-green-400">Insert Your API Keys</h3>
                    <p className="text-green-300 text-lg leading-relaxed mb-4">
                      Sign up for PayPal Business account, get your API credentials, and configure your .env file:
                    </p>
                    <div className="code-block">
                      <code>
                        PAYPAL_CLIENT_ID=your_client_id_here<br />
                        PAYPAL_CLIENT_SECRET=your_secret_here
                      </code>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="step-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-green-500">4</span>
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-2xl font-bold mb-3 text-green-400">Deploy Your Instance</h3>
                    <p className="text-green-300 text-lg leading-relaxed">
                      Deploy to GitHub Pages, Vercel, Netlify, or any hosting platform. Your instance is now live 
                      and ready to accept payments to YOUR PayPal account.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="step-5">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-green-500">5</span>
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-2xl font-bold mb-3 text-green-400">Share & Distribute</h3>
                    <p className="text-green-300 text-lg leading-relaxed">
                      Share your instance with your network, social media, email lists, or forums. 
                      Every person who pays $1 gets the same knowledge you got.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="card-tech p-8 hover:scale-105 transition-transform" data-testid="step-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                      <span className="text-2xl font-bold text-green-500">6</span>
                    </div>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-2xl font-bold mb-3 text-green-400">Scale to 1,000,000+</h3>
                    <p className="text-green-300 text-lg leading-relaxed">
                      With 330+ million people in the U.S. alone, if just 1% pay $1, that's over $3 million. 
                      The math is simple. The execution is up to you.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </section>

          {/* Why It's Legal */}
          <section className="mb-16" data-testid="legal-section">
            <h2 className="text-3xl font-bold mb-6 text-green-400 glow-text">WHY IT'S 100% LEGAL & ETHICAL</h2>
            <Card className="card-tech p-8">
              <div className="space-y-4 text-green-300 text-lg leading-relaxed">
                <p>
                  <span className="text-green-400 font-bold">You're not selling a scam or pyramid scheme.</span> 
                  You're selling information and open-source software under an MIT license.
                </p>
                <p>
                  <span className="text-green-400 font-bold">Every transaction is voluntary.</span> 
                  Users pay $1 knowing exactly what they're getting: source code, documentation, and knowledge.
                </p>
                <p>
                  <span className="text-green-400 font-bold">Full transparency.</span> 
                  The entire codebase is open source. No hidden fees, no upsells, no recurring charges.
                </p>
                <p>
                  <span className="text-green-400 font-bold">Replicable by design.</span> 
                  Every user has the right to fork, modify, and redistribute. That's the beauty of open source.
                </p>
              </div>
            </Card>
          </section>

          {/* CTA */}
          <div className="text-center" data-testid="cta-container">
            <Card className="card-tech p-12 pulse-glow">
              <h2 className="text-4xl font-bold mb-6 glow-text">READY TO BUILD YOUR MACHINE?</h2>
              <p className="text-xl text-green-300 mb-8">
                Get instant access for just $1 and start your journey today.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="btn-primary text-xl px-12 py-6"
                data-testid="get-started-button"
              >
                Get Started Now - $1
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;